import { Link } from "react-router-dom";
import { Seo } from "../components/site/Seo";
import { Reveal } from "../components/site/Shared";
import { PRIVACY_POLICY, TERMS, REFUND_POLICY } from "../constants/legal";
import { COMPANY } from "../constants/site";
import { Icon } from "../components/site/Icon";

const LegalDoc = ({ doc, path, seoTitle, seoDesc }) => (
  <>
    <Seo title={seoTitle} description={seoDesc} path={path} />
    <section className="container-site max-w-3xl py-20 lg:py-24">
      <Reveal>
        <h1 className="font-display text-[clamp(32px,4vw,48px)] font-semibold leading-[1.08] tracking-tight text-ink">
          {doc.title}
        </h1>
        <p className="mt-3 font-mono text-xs uppercase tracking-[0.14em] text-ink/45">{doc.updated}</p>
      </Reveal>
      <div className="mt-12 space-y-10">
        {doc.sections.map((s) => (
          <Reveal key={s.heading}>
            <h2 className="text-lg font-bold text-ink">{s.heading}</h2>
            {s.list && (
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-relaxed text-ink/70">
                {s.list.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}
            {s.body?.map((p, i) => (
              <p key={i} className="mt-3 text-sm leading-relaxed text-ink/70">{p}</p>
            ))}
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-14 rounded-2xl border border-teal-deep/10 bg-white p-6">
        <p className="text-sm text-ink/60">
          Questions about this document? Email{" "}
          <a href={`mailto:${COMPANY.supportEmail}`} data-testid="legal-contact-link" className="font-bold text-teal-deep underline underline-offset-4 hover:text-teal">
            {COMPANY.supportEmail}
          </a>
          .
        </p>
      </Reveal>
    </section>
  </>
);

export const PrivacyPage = () => (
  <LegalDoc
    doc={PRIVACY_POLICY}
    path="/privacy-policy"
    seoTitle="Privacy Policy"
    seoDesc="How HomeAligns collects, uses, protects and deletes your personal data — under India's Digital Personal Data Protection Act, 2023."
  />
);

export const TermsPage = () => (
  <LegalDoc
    doc={TERMS}
    path="/terms"
    seoTitle="Terms & Conditions"
    seoDesc="The terms that govern your use of HomeAligns — accounts, customers, service providers, marketplace, estimated values, AI features and payments."
  />
);

export const RefundPage = () => (
  <LegalDoc
    doc={REFUND_POLICY}
    path="/refund-policy"
    seoTitle="Refund & Cancellation Policy"
    seoDesc="How booking cancellations and refunds work on HomeAligns, and how plan payments are handled while payments run in Test Mode."
  />
);

export const AccountDeletionPage = () => (
  <>
    <Seo
      title="Account Deletion & Data Requests"
      description="How to download your HomeAligns data, delete your account, or raise a privacy request — from Profile → Privacy & data in the app, or via support@homealigns.com."
      path="/account-deletion"
    />
    <section className="container-site max-w-3xl py-20 lg:py-24">
      <Reveal>
        <p className="eyebrow mb-4">Your data, your call</p>
        <h1 className="font-display text-[clamp(32px,4vw,48px)] font-semibold leading-[1.08] tracking-tight text-ink">
          Account deletion & data requests.
        </h1>
        <p className="mt-5 text-base leading-relaxed text-ink/65">
          You can export or erase your HomeAligns data at any time. No emails back and forth
          required — it is built into the app.
        </p>
      </Reveal>

      <div className="mt-12 space-y-6">
        {[
          {
            icon: "Download",
            title: "Download your data",
            text: "In the app, go to Profile → Privacy & data and download a copy of your data.",
          },
          {
            icon: "Trash2",
            title: "Delete your account",
            text: "In the app, go to Profile → Privacy & data and choose deletion. Your account is deactivated immediately and erased 7 days after your request, unless you cancel within that window.",
          },
          {
            icon: "FileText",
            title: "What happens to records",
            text: "Booking, payment, subscription and invoice records may be kept for up to 8 years where accounting and tax laws require it, with your name, contact details and address removed. Support tickets are kept with identifiers removed for 12 months, then deleted.",
          },
          {
            icon: "Mail",
            title: "Prefer email?",
            text: `Write to ${COMPANY.supportEmail} with your request. Privacy and grievance requests are answered within 30 days. If you are not satisfied, you may approach the Data Protection Board of India.`,
          },
        ].map((s, i) => (
          <Reveal key={s.title} delay={(i % 2) + 1} className="card-site flex items-start gap-5 p-7">
            <span className="icon-tile mt-0.5 shrink-0">
              <Icon name={s.icon} className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-base font-bold text-ink">{s.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-ink/60">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-12">
        <Link to="/privacy-policy" data-testid="deletion-privacy-link" className="btn-secondary">
          Read the full Privacy Policy
        </Link>
      </Reveal>
    </section>
  </>
);
